import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '@edusense/shared-auth-core';
import {
  BarChart3, BookOpen, ChevronDown, ChevronRight, Files, Flag, FlaskConical,
  Globe2, Layers3, LifeBuoy, LogIn, LogOut, Megaphone, Menu, MessageSquareText,
  ShieldCheck, Ticket, UsersRound, X,
} from 'lucide-react';
import { startAdminPortalLogin } from '../auth/startAdminPortalLogin';
import { buildAdminRequestHeaders } from '../auth/adminRequestHeaders';
import { requestAdminNotifications } from '../auth/adminNotificationRequests';
import { getLayoutShellWidthClass } from './layoutWidth';
import {
  formatAdminNavNotificationCount,
  getAdminNavItems,
  getAdminNavItemNotificationCount,
  isAdminNavItemActive,
  type AdminNavItem,
  type AdminNavNotificationCounts,
} from '../navigation/adminNavigation';
import { ADMIN_APPLICATIONS, buildAdminApplicationPath, type AdminApplicationId } from '../apps/adminApplications';
import { useAdminApplicationScope } from '../apps/AdminApplicationScope';
import { buildFunnelImportantIssues, type FunnelSummaryResponse } from '../types/metrics';
import { countPendingSupportInquiries, normalizeSupportInquiries, type SupportInquiry } from '../types/support';

interface LayoutProps {
  children: ReactNode;
}

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const navigationPresentation: Record<string, { label: string; icon: typeof UsersRound }> = {
  Accounts: { label: '계정·구독', icon: UsersRound },
  Coupons: { label: '쿠폰', icon: Ticket },
  Metrics: { label: '이용 지표', icon: BarChart3 },
  '사용량·닢': { label: '사용량·닢', icon: BarChart3 },
  내부테스터: { label: '내부 테스터', icon: FlaskConical },
  'UGOT Pro': { label: 'UGOT Pro 권한·환불', icon: ShieldCheck },
  CMS: { label: '콘텐츠', icon: Files },
  Reports: { label: '신고 관리', icon: Flag },
  혜택: { label: '혜택 캠페인', icon: Ticket },
  Support: { label: '고객 문의', icon: LifeBuoy },
  '사이트 방문': { label: '사이트 방문', icon: Globe2 },
  책방: { label: '책방', icon: BookOpen },
  리뷰: { label: '리뷰', icon: MessageSquareText },
  공지: { label: '공지', icon: Megaphone },
};

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const { application, applicationId, selectApplication } = useAdminApplicationScope();
  const { user, token: accessToken, isAuthenticated: isLoggedIn, isLoading, logout } = useAuth();
  const shellWidthClass = getLayoutShellWidthClass(location.pathname);
  const [notificationCounts, setNotificationCounts] = useState<AdminNavNotificationCounts>({});
  const [notificationError, setNotificationError] = useState<string | null>(null);
  const mobileMenuRef = useRef<HTMLDialogElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigationItems = getAdminNavItems(applicationId);
  const activeNavigationItem = navigationItems.find((item) => isAdminNavItemActive(item, location.pathname));
  const activeNavigationLabel = activeNavigationItem
    ? navigationPresentation[activeNavigationItem.label]?.label ?? activeNavigationItem.label
    : '관리자';

  const closeMobileMenu = useCallback(() => {
    mobileMenuRef.current?.close();
    setMobileMenuOpen(false);
  }, []);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => {
      if (desktop.matches) closeMobileMenu();
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, [closeMobileMenu]);

  const getAuthHeaders = useCallback(() => buildAdminRequestHeaders(accessToken), [accessToken]);

  const loadNotificationCounts = useCallback(async (signal: AbortSignal) => {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const { results, authenticationFailure } = await requestAdminNotifications({
      getHeaders: getAuthHeaders,
      signal,
      request: (headers) => Promise.allSettled([
        axios.get<SupportInquiry[]>('/api/support/admin/inquiries', {
          headers,
          signal,
          withCredentials: true,
        }),
        applicationId === 'sajug'
          ? axios.get<FunnelSummaryResponse>('/api/v1/product-metrics/funnel', {
              params: {
                startDate: `${toDateInputValue(thirtyDaysAgo)}T00:00:00`,
                endDate: `${toDateInputValue(now)}T23:59:59`,
              },
              headers,
              signal,
              withCredentials: true,
            })
          : Promise.resolve(null),
      ] as const),
    });

    if (signal.aborted) return false;
    if (authenticationFailure) {
      setNotificationError(authenticationFailure === 'forbidden'
        ? '관리자 알림을 조회할 권한이 없습니다. 관리자 권한을 확인해 주세요.'
        : '관리자 세션을 확인할 수 없어 알림 갱신을 중단했습니다. 다시 로그인해 주세요.');
      return false;
    }
    if (!results) return false;
    const [supportResult, funnelResult] = results;
    setNotificationError(null);

    setNotificationCounts((previous) => ({
      support: supportResult.status === 'fulfilled'
        ? countPendingSupportInquiries(normalizeSupportInquiries(supportResult.value.data))
        : previous.support,
      metrics: funnelResult.status === 'fulfilled' && funnelResult.value
        ? buildFunnelImportantIssues(funnelResult.value.data?.total ?? {}).length
        : previous.metrics,
    }));
    return true;
  }, [applicationId, getAuthHeaders]);

  useEffect(() => {
    if (!isLoggedIn) {
      setNotificationCounts({});
      setNotificationError(null);
      return;
    }

    const controller = new AbortController();
    let timeoutId: number | undefined;
    const poll = async () => {
      const shouldContinue = await loadNotificationCounts(controller.signal);
      if (shouldContinue && !controller.signal.aborted) {
        timeoutId = window.setTimeout(() => { void poll(); }, 60_000);
      }
    };
    void poll();

    return () => {
      controller.abort();
      window.clearTimeout(timeoutId);
    };
  }, [isLoggedIn, loadNotificationCounts]);

  const renderNavigationLink = (item: AdminNavItem) => {
    const presentation = navigationPresentation[item.label];
    const Icon = presentation?.icon ?? Layers3;
    const label = presentation?.label ?? item.label;
    const active = isAdminNavItemActive(item, location.pathname);
    const notificationCount = getAdminNavItemNotificationCount(item, notificationCounts);
    return (
      <Link
        key={item.path}
        to={item.path}
        aria-current={active ? 'page' : undefined}
        onClick={closeMobileMenu}
        className={`flex min-h-10 items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
          active
            ? 'bg-[#254dc7] font-semibold text-white'
            : 'text-slate-300 hover:bg-white/7 hover:text-white'
        }`}
      >
        <Icon className={`h-[18px] w-[18px] shrink-0 ${active ? 'text-blue-100' : 'text-slate-400'}`} aria-hidden="true" />
        <span>{label}</span>
        {notificationCount > 0 ? (
          <span className="ml-auto rounded-md bg-rose-400/15 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-rose-200" aria-label={`알림 ${notificationCount}건`}>
            {formatAdminNavNotificationCount(notificationCount)}
          </span>
        ) : null}
      </Link>
    );
  };

  const renderSidebarContent = (mobile = false) => (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex h-20 shrink-0 items-center justify-between px-6">
        <Link to={buildAdminApplicationPath(applicationId)} onClick={closeMobileMenu} className="flex items-center gap-3 rounded-sm text-slate-50" aria-label="UGOT 관리자 홈">
          <Layers3 className="h-7 w-7 text-blue-300" strokeWidth={1.6} aria-hidden="true" />
          <span>
            <span className="block text-xl font-bold leading-6 tracking-[0.12em]">UGOT</span>
            <span className="block text-[10px] font-medium tracking-[0.2em] text-slate-400">ADMIN CONSOLE</span>
          </span>
        </Link>
        {mobile ? (
          <button type="button" className="flex h-10 w-10 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10" onClick={closeMobileMenu} aria-label="관리 메뉴 닫기">
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        ) : null}
      </div>

      <div className="shrink-0 px-4 pb-5">
        <label className="mb-2 block px-1 text-[11px] font-semibold tracking-wide text-slate-400" htmlFor={mobile ? 'mobile-admin-application' : 'admin-application'}>운영 서비스</label>
        <div className="relative">
          <select
            id={mobile ? 'mobile-admin-application' : 'admin-application'}
            className="h-11 w-full appearance-none rounded-lg border border-slate-600/70 bg-[#1e2b46] pl-3 pr-9 text-sm font-semibold text-slate-50 transition-colors hover:border-slate-400"
            aria-label="관리 앱 선택"
            value={applicationId}
            onChange={(event) => selectApplication(event.target.value as AdminApplicationId)}
          >
            {ADMIN_APPLICATIONS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-slate-400" aria-hidden="true" />
        </div>
      </div>

      <nav className="min-h-0 flex-1 space-y-6 overflow-y-auto px-4 pb-5" aria-label="관리 메뉴">
        <div>
          <p className="mb-2 px-3 text-[11px] font-semibold tracking-wide text-slate-400">서비스 관리</p>
          <div className="space-y-1">{navigationItems.filter((item) => item.scope === 'application').map(renderNavigationLink)}</div>
        </div>
        <div>
          <p className="mb-2 px-3 text-[11px] font-semibold tracking-wide text-slate-400">공통 운영</p>
          <div className="space-y-1">{navigationItems.filter((item) => item.scope !== 'application').map(renderNavigationLink)}</div>
        </div>
      </nav>

      <div className="shrink-0 border-t border-white/10 p-4">
        {isLoading ? (
          <div role="status" className="flex items-center gap-2 px-2 py-3 text-sm text-slate-300">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-slate-600 border-t-blue-300" aria-hidden="true" />
            로그인 확인 중
          </div>
        ) : isLoggedIn && user ? (
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-600 bg-slate-700/50 text-blue-200">
              <ShieldCheck className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold text-slate-200">관리자 계정</p>
              <p className="truncate text-[11px] text-slate-400" title={user.email || user.username}>{user.email || user.username}</p>
            </div>
            <button type="button" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-slate-300 hover:bg-white/10 hover:text-white" onClick={logout} aria-label="로그아웃" title="로그아웃">
              <LogOut className="h-[18px] w-[18px]" aria-hidden="true" />
            </button>
          </div>
        ) : (
          <Button className="w-full gap-2" onClick={() => startAdminPortalLogin()}>
            <LogIn className="h-4 w-4" aria-hidden="true" />로그인
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <a href="#admin-main-content" className="sr-only fixed left-4 top-3 z-[100] rounded-md bg-primary px-4 py-2 text-primary-foreground focus:not-sr-only">본문으로 바로 가기</a>
      <aside className="admin-sidebar fixed inset-y-0 left-0 z-40 hidden w-64 bg-[#142039] lg:block" aria-label="운영 탐색">
        {renderSidebarContent()}
      </aside>
      <dialog ref={mobileMenuRef} id="admin-mobile-navigation" className="admin-mobile-menu admin-sidebar bg-[#142039] text-slate-200" aria-label="관리 메뉴" onClose={() => setMobileMenuOpen(false)} onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeMobileMenu();
      }}>
        {renderSidebarContent(true)}
      </dialog>

      <div className="min-w-0 lg:pl-64">
        <header className="sticky top-0 z-30 border-b bg-card">
          <div className={`${shellWidthClass} flex min-h-16 items-center gap-3 py-3`}>
            <Button variant="ghost" size="icon" className="shrink-0 lg:hidden" aria-label="관리 메뉴 열기" aria-haspopup="dialog" aria-expanded={mobileMenuOpen} aria-controls="admin-mobile-navigation" onClick={() => {
              mobileMenuRef.current?.showModal();
              setMobileMenuOpen(true);
            }}>
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5 text-sm">
              <span className="text-muted-foreground">{activeNavigationItem?.scope === 'application' ? application.label : '공통 운영'}</span>
              <ChevronRight className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
              <span className="font-semibold text-foreground">{activeNavigationLabel}</span>
            </div>
            <span className="ml-auto hidden shrink-0 rounded-md border border-border bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground sm:inline-flex">
              {activeNavigationItem?.scope === 'application' ? `${application.shortLabel} 앱 범위` : '전체 서비스 공통'}
            </span>
          </div>
        </header>

        <main id="admin-main-content" tabIndex={-1} className={`${shellWidthClass} min-w-0 py-6 outline-none md:py-8`}>
          {notificationError ? <p role="alert" className="mb-4 text-sm text-destructive">{notificationError}</p> : null}
          {children}
        </main>
      </div>
    </div>
  );
}
